import { useState, useCallback } from 'react';
import { useTranslate } from 'react-admin';
import {
  Box,
  Avatar,
  Typography,
  IconButton,
  Button,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import BlockIcon from '@mui/icons-material/Block';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import type { Conversation, Ticket, TicketStatus } from '../../../types/chat.types';
import { useTicketActions } from '../../../hooks/useTicketActions';
import { useContactAvatarUrl } from '../../../hooks/useContactAvatarUrl';
import { TransferTicketDialog } from '../../ticket/TransferTicketDialog';
import { ChannelTypeIcon } from '../../../resources/contact/components/ChannelTypeIcon';

interface ChatHeaderProps {
  conversation: Conversation;
  onToggleDetails?: () => void;
  onMuteConversation?: () => void;
  onBlockContact?: () => void;
  showDetailsButton?: boolean;
  isDetailsOpen?: boolean;
  /** Current user ID for ticket actions */
  currentUserId?: number;
  /** Show ticket quick actions */
  showTicketActions?: boolean;
}

const TicketStatusChip = ({ ticket }: { ticket: Ticket }) => {
  const translate = useTranslate();

  const statusConfig: Record<TicketStatus, { color: 'error' | 'warning' | 'success' | 'info'; label: string }> = {
    pending: { color: 'info', label: translate('resources.ticket.status.pending') },
    open: { color: 'error', label: translate('resources.ticket.status.open') },
    resolved: { color: 'warning', label: translate('resources.ticket.status.resolved') },
    closed: { color: 'success', label: translate('resources.ticket.status.closed') },
  };

  const config = statusConfig[ticket.status];
  if (!config) return null;

  return (
    <Chip
      icon={<ConfirmationNumberIcon sx={{ fontSize: 14 }} />}
      label={`#${ticket.id} - ${config.label}`}
      size="small"
      color={config.color}
      variant="outlined"
      sx={{
        height: 24,
        fontSize: 11,
        fontWeight: 500,
        '& .MuiChip-icon': { fontSize: 14 },
      }}
    />
  );
};

const OnlineStatusIndicator = ({ isOnline }: { isOnline: boolean }) => {
  const translate = useTranslate();

  return (
    <Tooltip title={isOnline ? translate('resources.chat.online') : translate('resources.chat.offline')}>
      <FiberManualRecordIcon
        sx={{
          fontSize: 10,
          color: isOnline ? 'success.main' : 'grey.400',
          ml: 0.5,
        }}
      />
    </Tooltip>
  );
};

const ContactAvatar = ({
  conversation,
}: {
  conversation: Conversation;
}) => {
  const resolvedAvatarUrl = useContactAvatarUrl(conversation.contact?.avatarFileId);
  const avatarUrl = conversation.contact?.profilePicUrl || resolvedAvatarUrl;

  const getAvatarContent = () => {
    if (conversation.contact?.name) {
      return conversation.contact.name[0].toUpperCase();
    }
    if (conversation.name) {
      return conversation.name[0].toUpperCase();
    }
    return '?';
  };

  const getAvatarIcon = () => {
    if (conversation.chatbot) {
      return <SmartToyIcon />;
    }
    if (conversation.isGroup) {
      return <GroupIcon />;
    }
    return <PersonIcon />;
  };

  if (conversation.isGroup || conversation.chatbot) {
    return (
      <Avatar
        sx={{
          bgcolor: conversation.chatbot ? 'secondary.main' : 'primary.main',
          width: 40,
          height: 40,
        }}
      >
        {getAvatarIcon()}
      </Avatar>
    );
  }

  return (
    <Avatar
      src={avatarUrl ?? undefined}
      sx={{
        bgcolor: 'primary.light',
        width: 40,
        height: 40,
      }}
    >
      {getAvatarContent()}
    </Avatar>
  );
};

export function ChatHeader({
  conversation,
  onToggleDetails,
  onMuteConversation,
  onBlockContact,
  showDetailsButton = true,
  isDetailsOpen = false,
  currentUserId = 0,
  showTicketActions = true,
}: ChatHeaderProps) {
  const translate = useTranslate();
  const isMobile = useMediaQuery('(max-width:599px)');
  const isNarrowViewport = useMediaQuery('(max-width:1440px)');
  // Compact: mobile always, or narrow viewport (<=1440px like notebooks) with details panel open
  const isCompact = isMobile || (isNarrowViewport && isDetailsOpen);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const isMenuOpen = Boolean(menuAnchor);

  const { isLoading: isTicketActionLoading, activeAction, close, reopen, returnToPending, transfer } = useTicketActions();

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleMute = useCallback(() => {
    handleMenuClose();
    onMuteConversation?.();
  }, [handleMenuClose, onMuteConversation]);

  const handleBlock = useCallback(() => {
    handleMenuClose();
    onBlockContact?.();
  }, [handleMenuClose, onBlockContact]);

  // Determine display name with priority: contact > name
  const displayName = (() => {
    if (conversation.contact?.name) {
      return conversation.contact.name;
    }
    if (conversation.name) {
      return conversation.name;
    }
    return translate('resources.chat.unknownContact');
  })();

  const contactInfo = conversation.contact?.email || conversation.contact?.name || null;

  // Find active ticket (pending, open, or resolved) or closed ticket
  const activeTicket = conversation.tickets?.find(
    (ticket) => ticket.status === 'pending' || ticket.status === 'open' || ticket.status === 'resolved'
  );
  const closedTicket = !activeTicket ? conversation.tickets?.find((ticket) => ticket.status === 'closed') : null;

  // Determine online status based on contactLastSeenAt
  const isOnline = Boolean(
    conversation.contactLastSeenAt &&
      new Date(conversation.contactLastSeenAt).getTime() > Date.now() - 5 * 60 * 1000
  );

  // Check if user is assigned to active ticket
  const isAssignedToMe = activeTicket?.assignedUserId === currentUserId;
  const isOpenTicket = activeTicket?.status === 'open';
  const isPendingTicket = activeTicket?.status === 'pending';
  const isResolvedTicket = activeTicket?.status === 'resolved';
  const hasActiveTicket = Boolean(activeTicket && (isOpenTicket || isPendingTicket || isResolvedTicket));

  // Ticket action handlers
  const handleCloseTicket = useCallback(async () => {
    if (!activeTicket?.id) return;
    await close(activeTicket.id as number);
  }, [activeTicket, close]);

  const handleReturnToPending = useCallback(async () => {
    if (!activeTicket?.id) return;
    await returnToPending(activeTicket.id as number);
  }, [activeTicket, returnToPending]);

  const handleTransferClick = useCallback(() => {
    setTransferOpen(true);
  }, []);

  const handleTransfer = useCallback(async (data: { userId?: number; queueId?: number }) => {
    if (!activeTicket?.id) return;
    await transfer(activeTicket.id as number, data);
    setTransferOpen(false);
  }, [activeTicket, transfer]);

  const handleReopenTicket = useCallback(async () => {
    if (!closedTicket?.id) return;
    await reopen(closedTicket.id as number);
  }, [closedTicket, reopen]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        minHeight: 64,
      }}
    >
      {/* Left section: Avatar and info (clickable to open details) */}
      <Box
        display="flex"
        alignItems="center"
        gap={1.5}
        minWidth={0}
        flex={1}
        onClick={onToggleDetails}
        sx={{ cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: 'action.hover' }, py: 0.5, px: 0.5, mx: -0.5 }}
      >
        <ContactAvatar conversation={conversation} />

        <Box minWidth={0} flex={1}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              noWrap
              sx={{ maxWidth: '100%' }}
            >
              {displayName}
            </Typography>
            {!conversation.isGroup && !conversation.chatbot && (
              <OnlineStatusIndicator isOnline={isOnline} />
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            {contactInfo && contactInfo !== displayName && (
              <Typography variant="caption" color="textSecondary" noWrap>
                {contactInfo}
              </Typography>
            )}
            {conversation.channelConnection && (
              <Chip
                icon={<ChannelTypeIcon type={conversation.channelConnection.channel?.type} />}
                label={conversation.channelConnection.name || conversation.channelConnection.channel?.name}
                size="small"
                variant="outlined"
                sx={{
                  height: 22,
                  fontSize: 11,
                  '& .MuiChip-icon': { fontSize: 14 },
                }}
              />
            )}
            {activeTicket && <TicketStatusChip ticket={activeTicket} />}
            {closedTicket && <TicketStatusChip ticket={closedTicket} />}
          </Box>
        </Box>
      </Box>

      {/* Right section: Actions */}
      <Box display="flex" alignItems="center" gap={isCompact ? 0.5 : 1} flexShrink={0}>
        {/* Ticket Quick Actions for Closed Tickets */}
        {showTicketActions && closedTicket && (
          <>
            <Tooltip title={isCompact ? translate('resources.ticket.actions.reopen') : ''}>
              <span>
                <Button
                  onClick={handleReopenTicket}
                  disabled={isTicketActionLoading}
                  variant="outlined"
                  color="primary"
                  size={isCompact ? 'small' : 'medium'}
                  startIcon={!isCompact ? (
                    activeAction === 'reopen' ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <LockOpenIcon />
                    )
                  ) : undefined}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    px: isCompact ? 1 : 2,
                    minWidth: isCompact ? 38 : undefined,
                    minHeight: 38,
                  }}
                >
                  {isCompact ? (
                    activeAction === 'reopen' ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <LockOpenIcon fontSize="small" />
                    )
                  ) : (
                    translate('resources.ticket.actions.reopen')
                  )}
                </Button>
              </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          </>
        )}

        {/* Ticket Quick Actions for Active Tickets */}
        {showTicketActions && hasActiveTicket && (
          <>
            {/* Close Ticket */}
            {(isOpenTicket || isPendingTicket || isResolvedTicket) && (
              <Tooltip title={isCompact ? translate('resources.ticket.actions.close') : ''}>
                <span>
                  <Button
                    onClick={handleCloseTicket}
                    disabled={isTicketActionLoading}
                    variant="contained"
                    color="success"
                    size={isCompact ? 'small' : 'medium'}
                    startIcon={!isCompact ? (
                      activeAction === 'close' ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <CheckCircleOutlineIcon />
                      )
                    ) : undefined}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                      px: isCompact ? 1 : 2,
                      minWidth: isCompact ? 38 : undefined,
                      minHeight: 38,
                      boxShadow: 'none',
                      '&:hover': { boxShadow: 'none' },
                    }}
                  >
                    {isCompact ? (
                      activeAction === 'close' ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <CheckCircleOutlineIcon fontSize="small" />
                      )
                    ) : (
                      translate('resources.ticket.actions.close')
                    )}
                  </Button>
                </span>
              </Tooltip>
            )}

            {/* Return to Pending */}
            {isOpenTicket && isAssignedToMe && (
              <Tooltip title={isCompact ? translate('resources.ticket.actions.returnToPending') : ''}>
                <span>
                  <Button
                    onClick={handleReturnToPending}
                    disabled={isTicketActionLoading}
                    variant="outlined"
                    color="warning"
                    size={isCompact ? 'small' : 'medium'}
                    startIcon={!isCompact ? (
                      activeAction === 'return' ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <ReplayIcon />
                      )
                    ) : undefined}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                      px: isCompact ? 1 : 2,
                      minWidth: isCompact ? 38 : undefined,
                      minHeight: 38,
                    }}
                  >
                    {isCompact ? (
                      activeAction === 'return' ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <ReplayIcon fontSize="small" />
                      )
                    ) : (
                      translate('resources.ticket.actions.returnToPending')
                    )}
                  </Button>
                </span>
              </Tooltip>
            )}

            {/* Transfer Ticket */}
            <Tooltip title={isCompact ? translate('resources.ticket.actions.transfer') : ''}>
              <span>
                <Button
                  onClick={handleTransferClick}
                  disabled={isTicketActionLoading}
                  variant="outlined"
                  color="primary"
                  size={isCompact ? 'small' : 'medium'}
                  startIcon={!isCompact ? <SwapHorizIcon /> : undefined}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    px: isCompact ? 1 : 2,
                    minWidth: isCompact ? 38 : undefined,
                    minHeight: 38,
                  }}
                >
                  {isCompact ? (
                    <SwapHorizIcon fontSize="small" />
                  ) : (
                    translate('resources.ticket.actions.transfer')
                  )}
                </Button>
              </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          </>
        )}

        {showDetailsButton && (
          <Tooltip title={translate('resources.chat.toggleDetails')}>
            <IconButton
              onClick={onToggleDetails}
              size="small"
              color={isDetailsOpen ? 'primary' : 'default'}
              sx={{
                bgcolor: isDetailsOpen ? 'primary.light' : 'transparent',
                '&:hover': {
                  bgcolor: isDetailsOpen ? 'primary.light' : 'action.hover',
                },
              }}
            >
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>
        )}

        {/* More Options - only render when there are actual menu items available */}
        {(onMuteConversation || onBlockContact) && (
          <>
            <Tooltip title={translate('resources.chat.moreOptions')}>
              <IconButton
                onClick={handleMenuOpen}
                size="small"
                aria-controls={isMenuOpen ? 'chat-header-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={isMenuOpen ? 'true' : undefined}
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>

            <Menu
              id="chat-header-menu"
              anchorEl={menuAnchor}
              open={isMenuOpen}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {onMuteConversation && (
                <MenuItem onClick={handleMute}>
                  <ListItemIcon>
                    <NotificationsOffIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{translate('resources.chat.muteConversation')}</ListItemText>
                </MenuItem>
              )}

              {!conversation.isGroup && !conversation.chatbot && onBlockContact && <Divider />}
              {!conversation.isGroup && !conversation.chatbot && onBlockContact && (
                <MenuItem onClick={handleBlock} sx={{ color: 'error.main' }}>
                  <ListItemIcon>
                    <BlockIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText>{translate('resources.chat.blockContact')}</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </>
        )}

        {/* Transfer Ticket Dialog */}
        {activeTicket && (
          <TransferTicketDialog
            open={transferOpen}
            onClose={() => setTransferOpen(false)}
            onTransfer={handleTransfer}
            currentUserId={currentUserId}
            isLoading={activeAction === 'transfer'}
          />
        )}
      </Box>
    </Box>
  );
}
